import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'
import { GameIcon } from '../../../features/game'
import { Game } from '../../../features/game/types/Game'
import { useGetInternalConventionalItemsQuery } from '../../api/conventional-genres'
import { useSearchInternalGamesQuery } from '../../api/internal-games'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { ConventionalItemType } from '../../features/conventional-genres/types/InternalConventionalItem'
import {
  canSearchGames,
  defaultSelectorMarketIso,
  filterSearchedGames,
  getMarketPowerscore,
  isGameSelected,
  minSearchTermLength,
  searchDebounceMs,
  toggleGameSelection,
} from './internalGameSelectorHelpers'

type InternalGameSelectorDialogProps = {
  open: boolean
  title?: string
  selectedGames?: Game[]
  // The market each selected game was picked in; games without one count as picked in the default market.
  selectedMarketIsos?: Record<string, string>
  // 1 picks a game and closes at once; more shows a Done button.
  maxSelected?: number
  // Also offers games without a POWERSCORE in the market.
  allowAllGames?: boolean
  onClose: () => void
  // Also hands over the market each game was picked in.
  onDone: (games: Game[], marketIsos: Record<string, string>) => void
}

const compareNames = (a: { name?: string }, b: { name?: string }) => ((a.name ?? '') < (b.name ?? '') ? -1 : (a.name ?? '') > (b.name ?? '') ? 1 : 0)

export const InternalGameSelectorDialog: FC<InternalGameSelectorDialogProps> = ({
  open,
  title,
  selectedGames,
  selectedMarketIsos,
  maxSelected = 1,
  allowAllGames = false,
  onClose,
  onDone,
}) => {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')
  const [marketIso, setMarketIso] = useState(defaultSelectorMarketIso)
  const [subgenreId, setSubgenreId] = useState('')
  const [selected, setSelected] = useState<Game[]>([])
  const [pickedMarketIsos, setPickedMarketIsos] = useState<Record<string, string>>({})
  const isMultiple = maxSelected > 1

  // Only on opening, so a parent passing a new array on every render doesn't wipe the selection.
  const initialSelectedGames = useRef(selectedGames)
  initialSelectedGames.current = selectedGames
  const initialMarketIsos = useRef(selectedMarketIsos)
  initialMarketIsos.current = selectedMarketIsos
  useEffect(() => {
    if (!open) return
    setTerm('')
    setDebouncedTerm('')
    setSubgenreId('')
    setSelected(initialSelectedGames.current ?? [])
    setPickedMarketIsos(initialMarketIsos.current ?? {})
  }, [open])

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTerm(term.trim()), searchDebounceMs)
    return () => clearTimeout(timeout)
  }, [term])

  const marketsQuery = useGetInternalMarketsQuery(undefined, { skip: !open })
  const subgenresQuery = useGetInternalConventionalItemsQuery(ConventionalItemType.Subgenre, { skip: !open })
  const canSearch = canSearchGames(debouncedTerm, subgenreId)
  const searchQuery = useSearchInternalGamesQuery(
    { term: debouncedTerm, marketIso, conventionalSubgenreId: subgenreId || undefined },
    { skip: !open || !canSearch }
  )

  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort(compareNames), [marketsQuery.data])
  const subgenres = useMemo(() => [...(subgenresQuery.data ?? [])].sort(compareNames), [subgenresQuery.data])
  const games = useMemo(() => filterSearchedGames(searchQuery.data ?? [], marketIso, allowAllGames), [allowAllGames, marketIso, searchQuery.data])
  const isSearching = canSearch && (searchQuery.isFetching || debouncedTerm !== term.trim())

  const handleSelect = (game: Game) => {
    if (!isMultiple) {
      onDone([game], { [game.id]: marketIso })
      return
    }
    if (!isGameSelected(selected, game)) setPickedMarketIsos((current) => ({ ...current, [game.id]: marketIso }))
    setSelected((current) => toggleGameSelection(current, game, maxSelected))
  }

  const handleDone = () => onDone(selected, Object.fromEntries(selected.map(({ id }) => [id, pickedMarketIsos[id] ?? defaultSelectorMarketIso])))

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <GRDialogTitle onClose={onClose}>{title || t('internal-common:game_selector_title')}</GRDialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              autoFocus
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('internal-common:game_selector_search')}
              inputProps={{ 'aria-label': t('internal-common:game_selector_search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <TextField
              select
              size="small"
              label={t('internal-common:game_selector_subgenre')}
              value={subgenreId}
              onChange={(event) => setSubgenreId(event.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">{t('internal-common:game_selector_all_subgenres')}</MenuItem>
              {subgenres.map((subgenre) => (
                <MenuItem key={subgenre.id} value={subgenre.id}>
                  {subgenre.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('internal-common:market')}
              value={markets.length ? marketIso : ''}
              onChange={(event) => setMarketIso(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              {markets.map((market) => (
                <MenuItem key={market.iso} value={market.iso}>
                  {market.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {!allowAllGames && (
            <Typography variant="body2" color="text.secondary">
              {t('internal-common:game_selector_analyzed_only')}
            </Typography>
          )}
          {!canSearch ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              {t('internal-common:game_selector_enter_term', { count: minSearchTermLength })}
            </Typography>
          ) : isSearching ? (
            <Skeleton variant="rectangular" height={240} />
          ) : searchQuery.isError ? (
            <Typography color="error.main" textAlign="center" py={3}>
              {t('internal-common:game_selector_search_failed')}
            </Typography>
          ) : !games.length ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              {t('internal-common:game_selector_no_games')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('internal-common:icon')}</TableCell>
                  <TableCell>{t('internal-common:name')}</TableCell>
                  <TableCell>{t('internal-common:publisher')}</TableCell>
                  <TableCell align="right">{t('internal-common:powerscore')}</TableCell>
                  <TableCell>{t('internal-common:game_selector_subgenre')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {games.map((game) => {
                  const gameSelected = isGameSelected(selected, game)
                  const powerscore = getMarketPowerscore(game, marketIso)
                  return (
                    <TableRow key={game.id} selected={gameSelected}>
                      <TableCell>
                        <GameIcon src={game.getIcon() as string} gameName={game.name ?? ''} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">{game.name}</Typography>
                      </TableCell>
                      <TableCell>{game.artist || '-'}</TableCell>
                      <TableCell align="right">{powerscore ? Math.round(powerscore) : '-'}</TableCell>
                      <TableCell>{game.conventionalSubgenre || '-'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={gameSelected ? 'outlined' : 'contained'}
                          color={gameSelected ? 'error' : 'primary'}
                          disabled={!gameSelected && isMultiple && selected.length >= maxSelected}
                          onClick={() => handleSelect(game)}
                        >
                          {gameSelected ? t('internal-common:game_selector_deselect') : t('internal-common:game_selector_select')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {isMultiple && (
          <Box display="flex" flexWrap="wrap" gap={1} flexGrow={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {t('internal-common:game_selector_selected_count', { count: selected.length, max: maxSelected })}
            </Typography>
            {selected.map((game) => (
              <Chip key={game.id} size="small" label={game.name} onDelete={() => setSelected((current) => current.filter(({ id }) => id !== game.id))} />
            ))}
          </Box>
        )}
        <Button onClick={onClose}>{t('internal-common:cancel')}</Button>
        {isMultiple && (
          <>
            <Button color="error" disabled={!selected.length} onClick={() => setSelected([])}>
              {t('internal-common:game_selector_clear')}
            </Button>
            <Button variant="contained" onClick={handleDone}>
              {t('internal-common:done')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
